/**
 * Shared quest icon registry for tutorial quests.
 * Provides a stable set of icon keys that can be authored in training profiles
 * and resolved consistently inside the simulator UI.
 *
 * @module questIcons
 */

import React from 'react';
import {
  AimOutlined,
  ArrowLeftOutlined,
  BulbOutlined,
  CalculatorOutlined,
  CaretRightOutlined,
  CheckCircleFilled,
  CheckSquareOutlined,
  ClearOutlined,
  CloseOutlined,
  DollarOutlined,
  EnvironmentOutlined,
  ExperimentOutlined,
  FilePdfOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  GlobalOutlined,
  HeartOutlined,
  HomeOutlined,
  LineChartOutlined,
  PauseOutlined,
  PlayCircleOutlined,
  QuestionCircleOutlined,
  ReloadOutlined,
  RightOutlined,
  RiseOutlined,
  SafetyCertificateOutlined,
  SearchOutlined,
  SettingOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  ToolOutlined,
  TrophyOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';

const QUEST_ICON_DEFINITIONS = Object.freeze([
  { key: 'aim', label: 'Aim', Icon: AimOutlined },
  { key: 'arrowLeft', label: 'Arrow left', Icon: ArrowLeftOutlined },
  { key: 'experiment', label: 'Experiment', Icon: ExperimentOutlined },
  { key: 'bulb', label: 'Bulb', Icon: BulbOutlined },
  { key: 'calculator', label: 'Calculator', Icon: CalculatorOutlined },
  { key: 'caretRight', label: 'Caret right', Icon: CaretRightOutlined },
  { key: 'checkCircleFilled', label: 'Check circle', Icon: CheckCircleFilled },
  { key: 'setting', label: 'Setting', Icon: SettingOutlined },
  { key: 'checkSquare', label: 'Check square', Icon: CheckSquareOutlined },
  { key: 'clear', label: 'Clear', Icon: ClearOutlined },
  { key: 'close', label: 'Close', Icon: CloseOutlined },
  { key: 'dollar', label: 'Dollar', Icon: DollarOutlined },
  { key: 'environment', label: 'Environment', Icon: EnvironmentOutlined },
  { key: 'filePdf', label: 'PDF file', Icon: FilePdfOutlined },
  { key: 'fullscreen', label: 'Fullscreen', Icon: FullscreenOutlined },
  { key: 'fullscreenExit', label: 'Fullscreen exit', Icon: FullscreenExitOutlined },
  { key: 'global', label: 'Global', Icon: GlobalOutlined },
  { key: 'rise', label: 'Rise', Icon: RiseOutlined },
  { key: 'heart', label: 'Heart', Icon: HeartOutlined },
  { key: 'home', label: 'Home', Icon: HomeOutlined },
  { key: 'lineChart', label: 'Line chart', Icon: LineChartOutlined },
  { key: 'pause', label: 'Pause', Icon: PauseOutlined },
  { key: 'playCircle', label: 'Play circle', Icon: PlayCircleOutlined },
  { key: 'questionCircle', label: 'Question circle', Icon: QuestionCircleOutlined },
  { key: 'reload', label: 'Reload', Icon: ReloadOutlined },
  { key: 'right', label: 'Right arrow', Icon: RightOutlined },
  { key: 'safetyCertificate', label: 'Safety certificate', Icon: SafetyCertificateOutlined },
  { key: 'search', label: 'Search', Icon: SearchOutlined },
  { key: 'team', label: 'Team', Icon: TeamOutlined },
  { key: 'thunderbolt', label: 'Thunderbolt', Icon: ThunderboltOutlined },
  { key: 'tool', label: 'Tool', Icon: ToolOutlined },
  { key: 'trophy', label: 'Trophy', Icon: TrophyOutlined },
  { key: 'videoCamera', label: 'Video camera', Icon: VideoCameraOutlined },
]);

export const AVAILABLE_QUEST_ICONS = Object.freeze(
  [...QUEST_ICON_DEFINITIONS]
    .sort((left, right) => left.label.localeCompare(right.label))
    .map(({ key, label }) => ({ key, label }))
);

const QUEST_ICON_MAP = Object.freeze(
  Object.fromEntries(QUEST_ICON_DEFINITIONS.map((definition) => [definition.key, definition]))
);

/**
 * Resolve a quest icon key into a React icon element.
 * @param {string | null | undefined} iconKey Icon key stored on the quest.
 * @param {object} [props] Optional props forwarded to the icon component.
 * @returns {React.ReactNode | null} Icon element when the key is known.
 */
export const renderQuestIcon = (iconKey, props = {}) => {
  const definition = QUEST_ICON_MAP[iconKey];
  if (!definition) {
    return null;
  }

  return React.createElement(definition.Icon, props);
};